/*
 *
 *
 * This file contains functions to make API requests using axios.
 * It includes methods for GET, POST, PUT, and DELETE requests, along with error handling and response processing.
 * The functions utilize an axios client instance that is configured with interceptors for request and response handling.
 * The getResponseMethod function processes the response based on the status code and whether a loader should be shown.
 * The API endpoints and configurations are defined in separate files for better modularity and maintainability.
 *
 *
 */

import axiosClient from "./axiosApiClient"
import { NO_RECORDES_FOUND } from "./CONSTANTS"
import { handleResponseFromStatusCode } from "./handleResponseFromStatusCode"

const getResponseMethod = async (response, isShowLoader) => {
  if (isShowLoader === true || isShowLoader === false) {
    const out = await handleResponseFromStatusCode(response)
    return out
  }
  return response
}

/**
 * API Request Functions
 * - getRequest: Makes a GET request to the specified URL with optional axios configuration.
 * - postRequest: Makes a POST request to the specified URL with the given payload and optional axios configuration.
 * - putRequest: Makes a PUT request to the specified URL with the given payload and optional axios configuration.
 * - patchRequest: Makes a PATCH request to the specified URL with the given payload and optional axios configuration.
 * - deleteRequest: Makes a DELETE request to the specified URL with the given payload.
 *
 * Each function handles the response using the getResponseMethod, which processes the response based on status codes and loader visibility.
 * Errors are caught and processed to provide consistent error handling across all API requests.
 */

export function getRequest(URL, axiosConfig) {
  return axiosClient
    .get(`${URL}`, axiosConfig)
    .then((response) => getResponseMethod(response))
    .catch((error) => {
      if (
        error?.message ||
        error?.response?.data?.message === NO_RECORDES_FOUND
      ) {
        return error
      }
      return getResponseMethod(error?.response)
    })
}

export function postRequest(URL, payload, axiosConfig) {
  return axiosClient
    .post(`${URL}`, payload, axiosConfig)
    .then((response) => {
      return getResponseMethod(response, false)
    })
    .catch((error) => {
      return getResponseMethod(error?.response, false)
    })
}

export function putRequest(URL, payload, axiosConfig) {
  return axiosClient
    .put(`${URL}`, payload, axiosConfig)
    .then((response) => getResponseMethod(response))
    .catch((error) => {
      return getResponseMethod(error?.response)
    })
}

export function patchRequest(URL, payload, axiosConfig) {
  return axiosClient
    .patch(`${URL}`, payload, axiosConfig)
    .then((response) => getResponseMethod(response))
    .catch((error) => {
      return getResponseMethod(error?.response)
    })
}

export function deleteRequest(URL, payload) {
  return axiosClient
    .delete(`${URL}`, payload)
    .then((response) => getResponseMethod(response))
    .catch((error) => {
      return getResponseMethod(error?.response, true)
    })
}
